import React from 'react';
import { Link } from 'react-router-dom';
import {
  FILM_ROOM_LEAGUES,
  chaptersForLeague,
} from '@/data/filmRoomChapters';

interface FilmRoomSelectorProps {
  leagueSlug: string;
  activeChapterSlug: string;
  onLeagueSelect: (slug: string) => void;
  onChapterSelect?: (slug: string) => void;
  chapterHref?: (slug: string) => string;
  align?: 'center' | 'start';
}

const leagueClass = (active: boolean): string =>
  `min-h-11 rounded-lg px-5 text-sm font-heading font-bold uppercase tracking-wider transition-colors duration-150 ${
    active
      ? 'bg-accent-yellow text-primary'
      : 'border border-gray-700/60 bg-surface-card text-accent-light-blue hover:border-accent-yellow/50'
  }`;

const chapterClass = (active: boolean): string =>
  `inline-flex min-h-9 items-center rounded-full border px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors duration-150 ${
    active
      ? 'border-accent-yellow bg-accent-yellow/10 text-accent-yellow'
      : 'border-gray-700 bg-surface-card text-gray-400 hover:border-gray-500 hover:text-gray-200'
  }`;

const FilmRoomSelector: React.FC<FilmRoomSelectorProps> = ({
  leagueSlug,
  activeChapterSlug,
  onLeagueSelect,
  onChapterSelect,
  chapterHref,
  align = 'center',
}) => {
  const justifyClass = align === 'center' ? 'justify-center' : 'justify-start';

  return (
    <div className="space-y-3">
      <div
        role="group"
        aria-label="Choose a Film Room league"
        className={`flex flex-wrap gap-2 ${justifyClass}`}
      >
        {FILM_ROOM_LEAGUES.map((league) => {
          const active = league.slug === leagueSlug;
          return (
            <button
              key={league.slug}
              type="button"
              onClick={() => onLeagueSelect(league.slug)}
              className={leagueClass(active)}
              aria-pressed={active}
            >
              {league.label}
            </button>
          );
        })}
      </div>

      <div
        role="group"
        aria-label="Choose a Film Room player"
        className={`flex flex-wrap gap-2 ${justifyClass}`}
      >
        {chaptersForLeague(leagueSlug).map((chapter) => {
          const active = chapter.slug === activeChapterSlug;
          const className = chapterClass(active);

          if (chapterHref) {
            return (
              <Link
                key={chapter.slug}
                to={chapterHref(chapter.slug)}
                className={className}
                aria-current={active ? 'page' : undefined}
              >
                {chapter.player.nickname}
              </Link>
            );
          }

          return (
            <button
              key={chapter.slug}
              type="button"
              onClick={() => onChapterSelect?.(chapter.slug)}
              className={className}
              aria-pressed={active}
            >
              {chapter.player.nickname}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default FilmRoomSelector;
